<script setup lang="ts">
import { ref, computed, onUnmounted } from 'vue';
import NewHeader from '~/components/NewHeader.vue';
import NewFooter from '~/components/NewFooter.vue';
import AiToolLanding from '~/components/AiToolLanding.vue';
import EditorCTA from '~/components/EditorCTA.vue';
import api from '~/api';
import axios from 'axios';

const authStore = useAuthStore();
const router = useRouter();
const localePath = useLocalePath();

useHead({
  title: 'AI Sex Pose Generator - Best Face Swap',
  meta: [
    { name: 'description', content: 'Generate adult-themed AI poses from your reference photos. HD or FHD output, 8-12 credits per generation.' },
  ],
});

// 多段式落地页配置（参考 ai-undress 结构）
const ASSETS = 'https://cdn.treekee.com/ai-undress/images/ai-sex-pose-generator';
const config = {
  hero: {
    title: 'AI Sex Pose Generator',
    description: 'Upload reference photos and let AI craft custom adult poses with realistic detail. HD or FHD output in seconds.',
    videoSrc: `${ASSETS}/hero-pose.mp4`,
    videoPoster: `${ASSETS}/hero-pose.webp`,
  },
  stats: {
    items: [
      { value: '10+', label: 'Pose Styles' },
      { value: 'HD/FHD', label: 'Output Quality' },
      { value: '<60s', label: 'Generation Time' },
      { value: '100%', label: 'Privacy Safe' },
    ],
  },
  gallery: {
    title: 'Examples Generated by Our AI',
    description: 'Explore the variety of poses our AI can produce from your reference photos.',
    items: [
      { src: `${ASSETS}/example-pose-01.webp`, alt: 'AI sex pose example 1' },
      { src: `${ASSETS}/example-pose-02.webp`, alt: 'AI sex pose example 2' },
      { src: `${ASSETS}/example-pose-03.webp`, alt: 'AI sex pose example 3' },
    ],
  },
  imageFeatures: {
    title: 'Powered by Advanced AI',
    description: 'State-of-the-art models for natural, high-fidelity adult content generation.',
    items: [
      {
        title: 'Realistic Pose Generation',
        description: 'Natural body positioning with anatomically correct proportions.',
        video: { src: `${ASSETS}/feature-pose-gen.mp4`, poster: `${ASSETS}/feature-pose-gen.webp` },
      },
      {
        title: 'Multiple Style Options',
        description: 'Wide range of styles and scenes — pick what matches your vision.',
        video: { src: `${ASSETS}/feature-styles.mp4`, poster: `${ASSETS}/feature-styles.webp` },
      },
      {
        title: 'High-Resolution Output',
        description: 'HD or FHD output with sharp detail, ready for any creative project.',
        video: { src: `${ASSETS}/feature-quality.mp4`, poster: `${ASSETS}/feature-quality.webp` },
      },
    ],
  },
  usage: {
    title: 'How It Works',
    steps: [
      { title: 'Upload References', description: 'Add up to 3 reference images that capture the look you want.' },
      { title: 'Describe the Pose', description: 'Write a short prompt describing the pose, scene, and style.' },
      { title: 'Generate & Download', description: 'Pick HD or FHD, click generate, save the result.' },
    ],
  },
  faq: {
    title: 'Frequently Asked Questions',
    items: [
      { question: 'How many credits per generation?', answer: 'HD costs 8 credits, FHD costs 12. Failed generations are automatically refunded.' },
      { question: 'How long does it take?', answer: 'Most generations finish in 30 to 60 seconds, depending on quality and current load.' },
      { question: 'Are my uploads kept private?', answer: 'Uploads are stored only for processing and removed automatically once the task completes.' },
      { question: 'Can I use the result commercially?', answer: 'Generated content is yours. You are responsible for following local laws regarding adult AI content.' },
    ],
  },
  cta: {
    title: 'Ready to Try?',
    description: 'Upload a reference image and create your first AI pose in under a minute.',
    buttonText: 'Get Started',
    buttonHref: '#hero',
  },
};

// ===== 交互逻辑 =====
const POLL_INTERVAL = 60000;
const MAX_POLLS = 5;

const prompt = ref('');
const sourceImages = ref<string[]>([]);
const quality = ref<'HD' | 'FHD'>('HD');
const submitting = ref(false);
const polling = ref(false);
const pollTimer = ref<any>(null);
const resultUrl = ref('');
const errorMsg = ref('');
const orderId = ref('');
const taskId = ref('');
const isUploading = ref(false);

const cost = computed(() => quality.value === 'FHD' ? 12 : 8);
const canSubmit = computed(() =>
  !submitting.value && !polling.value && prompt.value.trim().length >= 3 && sourceImages.value.length > 0,
);

async function uploadFile(event: Event) {
  const input = event.target as HTMLInputElement;
  const files = Array.from(input.files || []);
  if (!files.length) return;
  if (sourceImages.value.length + files.length > 3) {
    alert('Up to 3 reference images.');
    return;
  }
  isUploading.value = true;
  try {
    for (const file of files) {
      const ext = (file.name.split('.').pop() || 'png').toLowerCase();
      const sign = await api.getSignUrl(ext);
      await axios.put(sign.sign_url, file, {
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
      });
      sourceImages.value.push((sign as any).file_url || `https://cdn.treekee.com/bestfaceswap/uploads/${sign.material_id}.${ext}`);
    }
  } catch (e) {
    console.error('upload failed', e);
    alert('Upload failed');
  } finally {
    isUploading.value = false;
    input.value = '';
  }
}

async function submit() {
  if (!authStore.isLogin) {
    router.push(localePath('/login/google?redirect=/sex-pose'));
    return;
  }
  errorMsg.value = '';
  resultUrl.value = '';
  submitting.value = true;
  try {
    const r = await api.createSexPose({
      prompt: prompt.value.trim(),
      image_urls: sourceImages.value,
      quality: quality.value,
    });
    orderId.value = r.order_id;
    taskId.value = r.task_id;
    submitting.value = false;
    polling.value = true;
    startPolling();
  } catch (e: any) {
    submitting.value = false;
    errorMsg.value = e?.message || 'Submission failed';
  }
}

function startPolling() {
  let attempts = 0;
  pollTimer.value = setInterval(async () => {
    attempts++;
    try {
      const r = await api.fetchSexPoseRes(orderId.value, taskId.value);
      if (r.task_status === 1) {
        resultUrl.value = r.url;
        stopPolling();
      } else if (r.task_status === -1) {
        errorMsg.value = r.message || 'Generation failed';
        stopPolling();
      } else if (attempts >= MAX_POLLS) {
        errorMsg.value = 'Timeout — task may still be processing';
        stopPolling();
      }
    } catch {/* ignore */}
  }, POLL_INTERVAL);
}
function stopPolling() {
  polling.value = false;
  if (pollTimer.value) { clearInterval(pollTimer.value); pollTimer.value = null; }
}
function reset() { resultUrl.value = ''; errorMsg.value = ''; taskId.value = ''; orderId.value = ''; }
onUnmounted(() => stopPolling());
</script>

<template>
  <div class="bg-neutral-950 min-h-screen">
    <NewHeader />
    <AiToolLanding :config="config">
      <template #form>
        <EditorCTA tool="sex-pose" />
        <div v-if="false" class="form-card">
          <div class="field">
            <label>Reference Images <span class="hint">(up to 3)</span></label>
            <div class="thumbs">
              <div v-for="(url, i) in sourceImages" :key="url" class="thumb">
                <img :src="url" alt="" />
                <button class="thumb-x" @click="sourceImages.splice(i, 1)">×</button>
              </div>
              <label v-if="sourceImages.length < 3" class="thumb-upload">
                <input type="file" accept="image/*" multiple class="hidden" :disabled="isUploading" @change="uploadFile" />
                <span v-if="isUploading">…</span><span v-else>+</span>
              </label>
            </div>
          </div>
          <div class="field">
            <label>Prompt <span class="req">*</span></label>
            <textarea v-model="prompt" rows="3" placeholder="Describe the pose / scene…" />
          </div>
          <div class="field">
            <label>Quality</label>
            <div class="seg">
              <button :class="{ active: quality === 'HD' }" @click="quality = 'HD'">HD · 8 credits</button>
              <button :class="{ active: quality === 'FHD' }" @click="quality = 'FHD'">FHD · 12 credits</button>
            </div>
          </div>

          <div v-if="resultUrl" class="result">
            <img :src="resultUrl" alt="Result" />
            <div class="actions">
              <a :href="resultUrl" target="_blank" rel="noopener" class="btn-secondary">Open</a>
              <button class="btn-secondary" @click="reset">New</button>
            </div>
          </div>
          <div v-else-if="polling" class="result-placeholder">
            <div class="spinner" /><p>Generating…</p>
          </div>

          <button class="cta-btn" :disabled="!canSubmit" @click="submit">
            <span v-if="submitting">Submitting…</span>
            <span v-else-if="polling">Generating… (30s–2min)</span>
            <span v-else>Generate · {{ cost }} credits</span>
          </button>
          <p v-if="errorMsg" class="err">{{ errorMsg }}</p>
        </div>
      </template>
    </AiToolLanding>
    <NewFooter />
  </div>
</template>

<style scoped lang="scss">
.form-card {
  @apply rounded-2xl border border-neutral-800 bg-neutral-900/60 p-6 space-y-4 text-white;
}
.field { @apply space-y-2; }
.field label { @apply text-sm font-medium block; }
.hint { @apply text-xs text-neutral-500; }
.req { @apply text-amber-400; }
.field textarea, .field input {
  @apply w-full rounded-md border border-neutral-700 bg-neutral-950/60 p-2.5 text-sm text-white placeholder-white/40
    focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500;
}
.thumbs { @apply flex flex-wrap gap-2; }
.thumb { @apply relative size-20 overflow-hidden rounded-lg border border-neutral-800; }
.thumb img { @apply size-full object-cover; }
.thumb-x { @apply absolute right-0.5 top-0.5 size-5 rounded-full bg-black/60 text-white text-sm leading-none; }
.thumb-upload {
  @apply flex size-20 cursor-pointer items-center justify-center rounded-lg border-2 border-dashed border-neutral-700
    text-white/50 hover:bg-neutral-900 text-3xl;
}
.seg { @apply flex gap-2; }
.seg button {
  @apply flex-1 rounded-full border border-neutral-700 bg-neutral-950/60 px-4 py-2 text-sm transition;
  &.active { @apply border-amber-500 bg-amber-500/10 text-amber-300; }
}
.cta-btn {
  @apply w-full rounded-full px-6 py-3 text-base font-semibold text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed;
  background: linear-gradient(135deg, #f97316 0%, #ea580c 50%, #c2410c 100%);
}
.err { @apply text-sm text-red-400; }
.result { @apply space-y-2; }
.result img { @apply w-full rounded-xl object-contain max-h-[400px]; }
.actions { @apply flex gap-2 justify-center; }
.btn-secondary {
  @apply inline-flex items-center justify-center rounded-full border border-neutral-700 px-4 py-2 text-sm hover:bg-neutral-800 cursor-pointer text-white;
}
.result-placeholder { @apply text-center text-neutral-500 py-6 space-y-2; }
.spinner { @apply mx-auto size-8 rounded-full border-2 border-neutral-700 border-t-amber-500 animate-spin; }
</style>
